import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AccountCircle } from '@mui/icons-material';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { EditPage, FuseInput, FuseSelect } from '/src/components';
import { useFilterStore, useUserStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { DataModelType, FormErrors, UpdateUserForm, UserRole } from '/src/lib/types';

/**
 * Edit User Page
 */
export const EditUserPage = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const filterStore = useFilterStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [formValues, setFormValues] = useState<UpdateUserForm>();
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /** Computed **/
  const user = userStore.currentUser;

  const companies = useMemo(() => filterStore.companyListGet() ?? [], [filterStore._companyList]);
  const lenders = useMemo(() => {
    const l = filterStore.lenderListGet();
    if (Array.isArray(l)) {
      return l;
    } else if (l && l._id) {
      return [l];
    }
    return [];
  }, [filterStore._lenderList]);

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    if (formValues) {
      setFormValues({
        ...formValues,
        [key]: value,
      });
      // Clear error when field is updated
      if (formErrors[key]) {
        setFormErrors({
          ...formErrors,
          [key]: '',
        });
      }
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = () => {
    const errors: FormErrors = {};

    if (!formValues?.username) {
      errors.username = 'Username is required';
    }
    if (!formValues?.firstName) {
      errors.firstName = 'First Name is required';
    }
    if (!formValues?.lastName) {
      errors.lastName = 'Last Name is required';
    }
    if (!formValues?.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formValues?.email)) {
      errors.email = 'Email is invalid';
    }
    if (!formValues?.roles[0]) {
      errors.role = 'User Type is required';
    }
    if (
      (formValues?.roles[0] === UserRole.Company || formValues?.roles[0] === UserRole.Investor) &&
      !formValues?.company
    ) {
      errors.company = 'Company is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Updates the User.
   *
   * @remarks
   * Redirects to the User's Detail page afterwards.
   */
  const update = async () => {
    if (!formValues) return;

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    try {
      const response = await userStore.update(formValues);
      if (!response) throw new Error(`Could not update User with ID "${formValues?._id}"`);

      toast.success(toastMessages.updateUser.success);
      navigate(`/users/${response._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.updateUser.error);
    }
  };

  const changeUserType = (role: UserRole) => {
    updateFormValue('roles')([role]);
    updateFormValue('company')(undefined);
  };

  /** Effects **/

  useEffect(() => {
    if (!params.userId) return;

    Promise.all([userStore.get(params.userId)]);
  }, [params.userId]);

  useEffect(() => {
    if (user) {
      setFormValues(user);
    }
  }, [user]);

  /** Render **/
  return (
    <EditPage
      modelType={DataModelType.User}
      onEdit={update}
      width="50%"
      editIcon={<AccountCircle className="md-icon material-icons" />}
    >
      <Box display="flex" justifyContent="center" alignItems="center">
        <FuseInput
          label="Username"
          value={formValues?.username}
          updateValue={updateFormValue('username')}
          error={formErrors.username}
          required
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        <FuseInput
          label="First Name"
          value={formValues?.firstName}
          updateValue={updateFormValue('firstName')}
          error={formErrors.firstName}
          required
        />
        <FuseInput
          label="Last Name"
          value={formValues?.lastName}
          updateValue={updateFormValue('lastName')}
          error={formErrors.lastName}
          required
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center">
        <FuseInput
          label="Email"
          type="email"
          value={formValues?.email}
          updateValue={updateFormValue('email')}
          error={formErrors.email}
          required
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center">
        <FuseSelect
          label="User Type"
          placeholder="Select User Type"
          value={formValues?.roles[0]}
          updateValue={changeUserType}
          error={formErrors.role}
          options={Object.entries(UserRole).map(([label, value]) => ({ label, value }))}
          required
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center">
        <FuseSelect
          label="User Company"
          placeholder="Select User Company"
          value={formValues?.company}
          updateValue={updateFormValue('company')}
          error={formErrors.company}
          options={(userStore.isLender ? lenders : companies).map((company) => ({
            label: company.name,
            value: company._id,
          }))}
          required
          disabled={user?.roles.includes(UserRole.Admin)}
          boxProps={{ 'aria-label': 'Select a Company to associate this user with.' }}
        />
      </Box>
    </EditPage>
  );
});
